import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { setManagerStoreId } from "../services/firebaseService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        console.log("🔐 User authenticated:", user.email, "UID:", user.uid);

        try {
          // First check if user is a superadmin (stored in top-level collection)
          const superAdminDocRef = doc(db, "superadmins", user.uid);
          const superAdminDoc = await getDoc(superAdminDocRef);

          if (superAdminDoc.exists()) {
            console.log("✅ User is superadmin");
            setUserRole("superadmin");
            setLoading(false);
            return;
          }

          // If not superadmin, check top-level users collection (simpler approach)
          console.log(`🔍 Checking top-level users collection for user: ${user.uid}`);
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.role;
            console.log("✅ User role found:", role, "| User data:", userData);
            
            // If manager, set the storeId from the document
            if (role === "manager" && userData.storeId) {
              setManagerStoreId(userData.storeId);
              console.log(`🏪 Set manager store ID to: ${userData.storeId}`);
            }
            
            setUserRole(role);
          } else {
            console.error("❌ NO ROLE DOCUMENT FOUND!");
            console.warn(
              `No role document found for user ${user.uid} at path: users/${user.uid}`
            );
            console.info(`🔧 FIX: Go to Firebase Console → Firestore Database`);
            console.info(`📝 Create document at path: users/${user.uid}`);
            console.info(
              `📋 Add fields: { role: "manager", email: "${user.email}", storeId: "scoressportsbar" }`
            );
            setUserRole(null);
          }
        } catch (error) {
          console.error("❌ Error fetching user role:", error);
          console.error("Error code:", error.code);
          console.error("Error message:", error.message);
          
          if (error.code === 'permission-denied' || error.message?.includes('permission')) {
            console.error("⚠️ Permission denied when reading user document");
            console.error("💡 Make sure Firestore rules allow authenticated users to read from users collection");
            console.error("💡 Rule should be: match /users/{userId} { allow read: if isAuthenticated(); }");
            console.error("💡 Also verify the rules have been deployed to Firebase");
          }
          
          setUserRole(null);
          setLoading(false);
          return; // Don't continue if there's an error
        }
      } else {
        console.log("👋 User logged out");
        setUserRole(null);
      }

      setLoading(false);
    });
    return unsubscribe;
  }, [auth, db]);

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    currentUser,
    userRole,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
